import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAIQuota } from "./useAIQuota";

export interface GeneratedQuestion {
  question: string;
  options: { id: string; text: string }[];
  correct_answer: string;
  explanation: string;
}

export interface GeneratePracticeInput {
  moduleTitle: string;
  category: string;
  originalQuestion: string;
  originalCorrectAnswer: string;
  originalExplanation?: string | null;
  difficulty?: string;
}

export interface GeneratePracticeResult {
  questions: GeneratedQuestion[];
  quota: { used: number; limit: number; remaining: number };
}

export type GenerateError =
  | { kind: "quota_exceeded"; message: string }
  | { kind: "rate_limit"; message: string }
  | { kind: "no_credits"; message: string }
  | { kind: "auth"; message: string }
  | { kind: "unknown"; message: string };

export function useGeneratePractice() {
  const { refresh } = useAIQuota();

  return useMutation<GeneratePracticeResult, GenerateError, GeneratePracticeInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke("generate-practice-questions", {
        body: input,
      });

      if (error) {
        const ctx: any = (error as any).context;
        let parsed: any = null;
        try {
          if (ctx && typeof ctx.json === "function") parsed = await ctx.json();
        } catch {
          /* ignore */
        }
        const status = ctx?.status ?? 0;
        const code = parsed?.error;
        const msg = parsed?.message || error.message || "Erro desconhecido";

        if (status === 401) throw { kind: "auth", message: "Sessão expirada. Faça login novamente." } as GenerateError;
        if (status === 429 && code === "quota_exceeded")
          throw { kind: "quota_exceeded", message: msg } as GenerateError;
        if (status === 429) throw { kind: "rate_limit", message: msg } as GenerateError;
        if (status === 402) throw { kind: "no_credits", message: msg } as GenerateError;
        throw { kind: "unknown", message: msg } as GenerateError;
      }

      return data as GeneratePracticeResult;
    },
    onSuccess: () => {
      refresh();
    },
  });
}
